// usePackages.ts
import { useEffect, useState } from 'react';

export type PackageRow = {
    category: string;
    id: string;             // = linkId
    title: string;
    sub_title?: string;
    price_text: string;     // как показываем в UI
    price_value: number;    // можно оставить, вдруг пригодится
    text_for_link: string;  // title для PriceLink
};

export type PackageGroup = {
    category: string;
    items: PackageRow[]; // порядок = как в CSV
};

const PACKAGES_CSV_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQf6mo7sZsSZm6cDqfimmqiwFX24c7tuMI6-IeVElce4dCFD4YjjiP_XAc7J1loHHOZ9s72roV2RcKk/pub?gid=1171228405&single=true&output=csv';
const VERSION_CSV_URL  = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQf6mo7sZsSZm6cDqfimmqiwFX24c7tuMI6-IeVElce4dCFD4YjjiP_XAc7J1loHHOZ9s72roV2RcKk/pub?gid=1804011488&single=true&output=csv';

const LS_KEY = 'packages_from_sheet_v1'; // { version, data }

function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let i = 0, cell = '', row: string[] = [], inQ = false;
    while (i < text.length) {
        const ch = text[i];
        if (inQ) {
            if (ch === '"') {
                if (i + 1 < text.length && text[i + 1] === '"') { cell += '"'; i += 2; }
                else { inQ = false; i++; }
            } else { cell += ch; i++; }
        } else {
            if (ch === '"') { inQ = true; i++; }
            else if (ch === ',') { row.push(cell); cell = ''; i++; }
            else if (ch === '\r') { i++; }
            else if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; i++; }
            else { cell += ch; i++; }
        }
    }
    row.push(cell); rows.push(row);
    if (rows.length && rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === '') rows.pop();
    return rows;
}

function priceToNumber(s: string): number {
    if (!s) return Number.POSITIVE_INFINITY;
    let t = s.replace(/\u00A0/g, '');
    t = t.replace(/\s/g, '');
    t = t.replace(/^[^\d\-+]+/, '');
    if (t.includes('.') && t.includes(','))  t = t.replace(/,/g, '');
    else if (t.includes(',') && !t.includes('.')) t = t.replace(/,/g, '.');
    const n = parseFloat(t);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function buildRows(csv: string): PackageRow[] {
    const rows = parseCsv(csv);
    if (!rows.length) return [];
    const header = rows[0].map(h => h.trim().toLowerCase());
    const idx = (n: string) => header.indexOf(n);

    const c = idx('category');
    const i = idx('id');
    const t = idx('title');
    const s = idx('sub_title');
    const p = idx('price_text');

    const out: PackageRow[] = [];
    for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        const category   = (row[c] ?? '').trim();
        const id         = (row[i] ?? '').trim();
        const title      = (row[t] ?? '').trim();
        const sub_title  = (row[s] ?? '').trim();
        const price_text = (row[p] ?? '').trim();
        if (!category || !id || !title) continue;

        const text_for_link = title;

        out.push({
            category,
            id,
            title,
            sub_title: sub_title || undefined,
            price_text,
            price_value: priceToNumber(price_text),
            text_for_link,
        });
    }
    return out;
}

async function fetchText(url: string) {
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) throw new Error(String(res.status));
    return res.text();
}

export function usePackages() {
    const [groups, setGroups] = useState<PackageGroup[]>([]);
    const [loading, setLoading] = useState(true);
    const [version, setVersion] = useState<string>('init');

    useEffect(() => {
        (async () => {
            setLoading(true);

            let remoteVersion = 'init';
            try {
                const verCsv = await fetchText(VERSION_CSV_URL);
                const v = parseCsv(verCsv)[0]?.[0] || 'init';
                remoteVersion = String(v).trim() || 'init';
            } catch {
                remoteVersion = 'force-refresh';
            }

            setVersion(remoteVersion);

            try {
                const raw = localStorage.getItem(LS_KEY);
                if (raw) {
                    const cached = JSON.parse(raw);
                    if (cached?.version === remoteVersion && Array.isArray(cached.data)) {
                        setGroups(cached.data);
                        setLoading(false);
                        return;
                    }
                }
            } catch {}

            try {
                const csv = await fetchText(PACKAGES_CSV_URL);
                const items = buildRows(csv);

                // группировка без сортировки
                const map = new Map<string, PackageRow[]>();
                for (const it of items) {
                    if (!map.has(it.category)) map.set(it.category, []);
                    map.get(it.category)!.push(it);
                }

                const grouped: PackageGroup[] = [];
                // @ts-ignore
                for (const [category, arr] of map.entries()) {
                    grouped.push({ category, items: arr });
                }

                setGroups(grouped);
                localStorage.setItem(LS_KEY, JSON.stringify({ version: remoteVersion, data: grouped }));
            } catch {
                setGroups([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return { groups, loading, version };
}
